import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { services } from '@/mocks/services';

const ServiceDetailPage = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const svc = services.find((s) => s.id === serviceId);

  useEffect(() => { window.scrollTo(0, 0); }, [serviceId]);

  if (!svc) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-xl mb-4">Service not found.</p>
          <Link to="/services" className="text-brand-amber underline">View All Services</Link>
        </div>
      </div>
    );
  }

  const related = services.filter((s) => s.category === svc.category && s.id !== svc.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-brand-cream font-sans">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[60vh] min-h-[400px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={svc.heroImage} alt={`${svc.name} - Valley View Construction Elkhart Indiana`} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-black/90 via-brand-black/40 to-brand-black/20" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pb-12 pt-32">
          <nav className="flex items-center gap-2 text-xs text-white/50 mb-4 flex-wrap">
            <Link to="/" className="hover:text-brand-amber transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/services" className="hover:text-brand-amber transition-colors">Services</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white/80">{svc.name}</span>
          </nav>
          <span className="inline-block px-3 py-1 bg-brand-amber/90 rounded-md text-white text-xs font-semibold mb-3">{svc.category}</span>
          <h1 className="font-serif text-4xl md:text-6xl font-bold text-white mb-3">{svc.name}</h1>
          <p className="text-white/75 text-lg italic font-serif">{svc.tagline}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <article className="lg:col-span-2 space-y-12">
            {/* Description */}
            <section>
              <h2 className="font-serif text-3xl font-bold text-brand-dark mb-4">About This Service</h2>
              <p className="text-brand-stone text-base leading-relaxed">{svc.description}</p>
            </section>

            {/* Benefits */}
            <section>
              <h2 className="font-serif text-2xl font-bold text-brand-dark mb-6">Why Invest in <span className="text-brand-amber">{svc.name}?</span></h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {svc.benefits.map((b) => (
                  <div key={b.title} className="flex items-start gap-4 p-5 bg-white rounded-xl border border-brand-cream-dark">
                    <div className="w-10 h-10 flex items-center justify-center bg-brand-amber/12 rounded-xl flex-shrink-0">
                      <i className={`${b.icon} text-brand-amber text-lg`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-brand-dark text-sm mb-1">{b.title}</h3>
                      <p className="text-brand-stone text-xs leading-relaxed">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-5 bg-brand-amber/10 border border-brand-amber/25 rounded-xl flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-brand-amber rounded-xl flex-shrink-0">
                  <i className="ri-arrow-up-circle-fill text-white text-2xl" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-brand-amber uppercase tracking-wider">Property Value Boost</p>
                  <p className="text-3xl font-bold font-serif text-brand-dark">{svc.propertyValueBoost}</p>
                  <p className="text-xs text-brand-stone">Estimated increase in property value with quality {svc.name.toLowerCase()}</p>
                </div>
              </div>
            </section>

            {/* Process */}
            <section>
              <h2 className="font-serif text-2xl font-bold text-brand-dark mb-6">Our Process</h2>
              <div className="space-y-4">
                {svc.process.map((step, i) => (
                  <div key={i} className="flex items-start gap-4 p-5 bg-white rounded-xl border border-brand-cream-dark">
                    <div className="w-9 h-9 flex items-center justify-center bg-brand-dark rounded-full flex-shrink-0">
                      <span className="text-brand-amber font-bold text-sm">{i + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-brand-dark text-sm mb-1">{step.step}</h3>
                      <p className="text-brand-stone text-xs leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Gallery */}
            {svc.gallery.length > 0 && (
              <section>
                <h2 className="font-serif text-2xl font-bold text-brand-dark mb-6">Project Gallery</h2>
                <div className="grid grid-cols-2 gap-4">
                  {svc.gallery.map((img, i) => (
                    <div key={i} className={`rounded-xl overflow-hidden ${i === 0 ? 'col-span-2 h-64' : 'h-44'}`}>
                      <img src={img} alt={`${svc.name} project ${i + 1} by Valley View Construction`} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* FAQ */}
            <section>
              <h2 className="font-serif text-2xl font-bold text-brand-dark mb-6">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {svc.faq.map((item, i) => (
                  <details key={i} className="group bg-white rounded-xl border border-brand-cream-dark overflow-hidden">
                    <summary className="flex items-center justify-between p-5 cursor-pointer list-none font-semibold text-brand-dark text-sm">
                      <strong>{item.q}</strong>
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-add-line group-open:hidden text-brand-amber" />
                        <i className="ri-subtract-line hidden group-open:block text-brand-amber" />
                      </div>
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-brand-stone text-sm leading-relaxed">{item.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>
          </article>

          {/* Sidebar */}
          <aside className="space-y-6">
            {/* CTA Card */}
            <div className="bg-brand-dark rounded-2xl p-7 sticky top-24">
              <h3 className="font-serif text-xl font-bold text-white mb-2">Get a Free Quote</h3>
              <p className="text-white/60 text-sm mb-6">Ready for your {svc.name.toLowerCase()} project? Contact us today for a free consultation.</p>
              <a href="tel:+15744096118" className="w-full flex items-center justify-center gap-2 py-3.5 bg-brand-amber hover:bg-brand-amber-light text-white font-semibold rounded-lg transition-colors mb-3">
                <i className="ri-phone-fill" /> 574-409-6118
              </a>
              <Link to="/#contact" className="w-full flex items-center justify-center gap-2 py-3.5 border border-white/20 hover:border-brand-amber text-white/80 hover:text-brand-amber font-semibold rounded-lg transition-all text-sm">
                <i className="ri-mail-line" /> Send Us a Message
              </Link>
              <div className="mt-6 pt-5 border-t border-white/10 space-y-3">
                {svc.whyUs.map((w, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-brand-amber text-xs" />
                    </div>
                    <span className="text-white/65 text-xs">{w}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Related Services */}
            {related.length > 0 && (
              <div>
                <h3 className="font-serif text-lg font-bold text-brand-dark mb-4">Related Services</h3>
                <div className="space-y-3">
                  {related.map((r) => (
                    <Link key={r.id} to={`/services/${r.id}`} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-brand-cream-dark hover:border-brand-amber/30 transition-all group">
                      <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 overflow-hidden rounded-lg">
                        <img src={r.image} alt={r.name} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-brand-dark text-sm truncate">{r.name}</p>
                        <p className="text-brand-stone text-xs">{r.category}</p>
                      </div>
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-right-line text-brand-amber group-hover:translate-x-1 transition-transform" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Service Area Badge */}
            <div className="p-5 bg-brand-amber/8 border border-brand-amber/20 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-map-pin-2-fill text-brand-amber" />
                </div>
                <span className="font-semibold text-brand-dark text-sm">30-Mile Service Radius</span>
              </div>
              <p className="text-brand-stone text-xs">Serving Elkhart, Goshen, South Bend, Mishawaka, Granger, and all of Michiana.</p>
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ServiceDetailPage;
